/**
 * Unified Analytics Service
 * Supports Segment, Mixpanel, both, or neither based on environment configuration
 * Similar to the backend Go implementation
 */

import mixpanel from "mixpanel-browser";

class AnalyticsService {
  constructor() {
    this.provider = null;
    this.segmentEnabled = false;
    this.mixpanelEnabled = false;
    this.isInitialized = false;
  }

  /**
   * Initialize analytics based on VUE_APP_ANALYTICS_PROVIDER
   * Providers: 'segment', 'mixpanel', 'both', 'none'
   */
  init() {
    if (this.isInitialized) {
      return;
    }

    this.provider = process.env.VUE_APP_ANALYTICS_PROVIDER || "mixpanel";

    // Initialize Segment if needed
    if (this.provider === "segment" || this.provider === "both") {
      if (process.env.VUE_APP_SEGMENT && window.analytics) {
        this.segmentEnabled = true;
      }
    }

    // Initialize Mixpanel if needed
    if (this.provider === "mixpanel" || this.provider === "both") {
      const mixpanelToken = process.env.VUE_APP_MIXPANEL_TOKEN;
      if (mixpanelToken) {
        const config = {
          debug: process.env.VUE_APP_ENV === "local",
          track_pageview: false, // We'll handle page tracking manually
          persistence: "localStorage",
        };

        // EU residency support
        if (process.env.VUE_APP_MIXPANEL_EU_RESIDENCY === "true") {
          config.api_host = "https://api-eu.mixpanel.com";
        }

        mixpanel.init(mixpanelToken, config);
        this.mixpanelEnabled = true;
      }
    }

    this.isInitialized = true;
  }

  /**
   * Identify user
   * @param {string} userId - User ID
   * @param {object} traits - User traits/properties
   * @param {object} options - Additional options (e.g., integrations)
   */
  identify(userId, traits = {}, options = {}) {
    if (!this.isInitialized) {
      return;
    }

    // Send to Segment
    if (this.segmentEnabled && window.analytics) {
      window.analytics.identify(userId, traits, options);
    }

    // Send to Mixpanel
    if (this.mixpanelEnabled) {
      mixpanel.identify(userId);
      if (Object.keys(traits).length > 0) {
        mixpanel.people.set(traits);
      }
    }
  }

  /**
   * Track event
   * @param {string} eventName - Event name
   * @param {object} properties - Event properties
   */
  track(eventName, properties = {}) {
    if (!this.isInitialized) return;

    // Send to Segment
    if (this.segmentEnabled && window.analytics) {
      window.analytics.track(eventName, properties);
    }

    // Send to Mixpanel
    if (this.mixpanelEnabled) {
      mixpanel.track(eventName, properties);
    }
  }

  /**
   * Track page view
   * @param {string} name - Page name
   * @param {object} properties - Page properties
   */
  page(name, properties = {}) {
    if (!this.isInitialized) return;

    // Send to Segment
    if (this.segmentEnabled && window.analytics) {
      window.analytics.page(name, properties);
    }

    // Send to Mixpanel
    if (this.mixpanelEnabled) {
      mixpanel.track_pageview({
        page: name,
        ...properties,
      });
    }
  }

  /**
   * Track group (team)
   * @param {string} groupId - Group/Team ID
   * @param {object} traits - Group traits
   */
  group(groupId, traits = {}) {
    if (!this.isInitialized) {
      return;
    }

    // Send to Segment
    if (this.segmentEnabled && window.analytics) {
      window.analytics.group(groupId, traits);
    }

    // Send to Mixpanel (using group profile)
    if (this.mixpanelEnabled) {
      mixpanel.set_group("team", groupId);
      if (Object.keys(traits).length > 0) {
        mixpanel.get_group("team", groupId).set(traits);
      }
    }
  }

  /**
   * Reset/logout user
   */
  reset() {
    if (!this.isInitialized) return;

    // Reset Segment
    if (this.segmentEnabled && window.analytics) {
      window.analytics.reset();
    }

    // Reset Mixpanel
    if (this.mixpanelEnabled) {
      mixpanel.reset();
    }
  }
}

// Export singleton instance
export default new AnalyticsService();
